import { logger } from '@upflowy/logger';
import { ConsumerEventType, StepDoc } from '@upflowy/types';
import React, { MouseEventHandler, useCallback, useRef } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { State } from 'xstate';

import InnerHTML from './InnerHTML';

import {
  EVENT_TYPE_SUBMIT,
  formValuesToJSON,
  handleClickEventOfNonType,
  handleSubmitEvent,
  secureValues,
} from '../helpers';
import { IContext, StateEvent, StateEventCallback, StepAssetType } from '../types';

import './style.css';

type FlowStepProps = {
  context: Record<string, unknown>;
  visible: boolean;
  state: State<IContext, StateEvent>;
  step: StepDoc;
  stateEventCallback: StateEventCallback;
  assets: StepAssetType[];
};

function FlowStep({ step, visible, state, context, stateEventCallback, assets }: FlowStepProps) {
  const flowRef = useRef<HTMLDivElement>(null);

  const formClickCallback = useCallback<MouseEventHandler<HTMLDivElement>>(
    (mouseEvent) => {
      if (!(mouseEvent.target instanceof HTMLElement)) return;
      const type =
        mouseEvent.target.getAttribute('data-xstate-event') ??
        handleClickEventOfNonType(mouseEvent)?.toUpperCase();
      if (type) {
        /*
      Filling up the context with form's input values
      namespace each attribute with the state (full path to handle nested states)
      */

        const clickId = mouseEvent.target.getAttribute('data-clickid');

        const event: StateEvent = {
          type: type as ConsumerEventType,
          data: null,
          meta: null,
          secure: {},
          redirectUrl: null,
          clickId,
          source: 'form',
        };

        if (type.startsWith(EVENT_TYPE_SUBMIT)) {
          const form = flowRef?.current?.querySelector<HTMLFormElement>('form');

          // prevent processing when form isn't valid
          if (!form?.getAttributeNames().includes('novalidate') && !form?.checkValidity()) {
            return;
          }

          event.data = form ? formValuesToJSON(form) : {};
          event.secure = secureValues(form);
        }

        try {
          stateEventCallback(event);
        } catch (e) {
          logger.logException(e);
        }
      }
    },
    [stateEventCallback],
  );

  return (
    <div
      role="presentation"
      style={{ display: visible ? '' : 'none' }}
      onClick={formClickCallback}
      onSubmit={handleSubmitEvent}
      ref={flowRef}
    >
      <SwitchTransition mode="out-in">
        <CSSTransition key={step.id} classNames="step-block" timeout={300}>
          <InnerHTML
            stepId={step.id}
            stepTitle={step.title}
            state={`${state.value}`}
            html={step.html}
            context={context}
            style={{ visibility: visible ? 'visible' : 'hidden' }}
            assets={assets}
          />
        </CSSTransition>
      </SwitchTransition>
    </div>
  );
}

export default FlowStep;
